// In src/components/InviteMemberModal.js

import React, { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import QRCode from 'qrcode';
import { Modal, Box, Typography, Button, TextField, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  textAlign: 'center', // Center content
};

function InviteMemberModal({ open, onClose }) {
  const { userProfile } = useAuth();
  const canvasRef = useRef(null); // Ref to hold the canvas element for the QR code

  // This is the special registration link for the admin's church
  const inviteLink = userProfile?.churchId ? `https://${window.location.host}/register?churchId=${userProfile.churchId}` : '';

  // This effect runs when the modal opens and generates the QR code
  useEffect(() => {
    console.log("InviteMemberModal useEffect triggered.");
    console.log("Modal Open:", open);
    console.log("User Profile:", userProfile);
    console.log("User Church ID:", userProfile?.churchId);
    console.log("Canvas Ref Current:", canvasRef.current);
    console.log("Invite Link:", inviteLink);

    // MODIFIED: Defer QR code generation to ensure canvasRef.current is available
    if (open && userProfile?.churchId && inviteLink) {
      console.log("All non-canvas conditions met. Attempting to generate QR code with timeout...");
      setTimeout(() => { // Defer execution
        if (canvasRef.current) { // Check canvasRef.current *inside* the timeout
          QRCode.toCanvas(canvasRef.current, inviteLink, { width: 256 }, (error) => {
            if (error) {
              console.error("Error generating QR code:", error);
            } else {
              console.log("QR code generated successfully. Canvas should be visible.");
            }
          });
        } else {
          console.log("QR code generation skipped in timeout: canvasRef.current is still null.");
        }
      }, 0); // Defer to next tick of event loop
    } else if (open) {
        // Log why QR code might not be generating if modal is open but other conditions fail
        console.log("QR code generation skipped because not all conditions are met (initial check):");
        if (!userProfile?.churchId) console.log("- userProfile.churchId is missing or null.");
        if (!inviteLink) console.log("- inviteLink is empty (likely due to missing churchId or window.location.host issue).");
    }
  }, [open, userProfile, inviteLink]);
  
  const handleCopyToClipboard = () => {
    if (!inviteLink) {
      alert('Invite link is not available yet.');
      return;
    }

    // --- Fallback logic for navigator.clipboard ---
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(inviteLink).then(() => {
        alert('Invite link copied to clipboard!');
      }, (err) => {
        console.error('Could not copy text using navigator.clipboard: ', err);
        // Fallback to execCommand if navigator.clipboard fails
        fallbackCopyToClipboard(inviteLink);
      });
    } else {
      // Fallback for environments where navigator.clipboard is not available
      fallbackCopyToClipboard(inviteLink);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Avoid scrolling to bottom
    textarea.style.left = '-9999px'; // Hide from view
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      alert('Invite link copied to clipboard (fallback)!');
    } catch (err) {
      console.error('Fallback copy failed: ', err);
      alert('Failed to copy link automatically. Please copy it manually.');
    } finally {
      document.body.removeChild(textarea);
    }
  };


  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2">
          Invite a New Member
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
          Have a new member scan this QR code with their phone's camera to register.
        </Typography>
        
        {/* Only render canvas if inviteLink is available.
            The canvas element must be in the DOM for canvasRef.current to be non-null. */}
        {inviteLink ? (
            <canvas ref={canvasRef} />
        ) : (
            <Box sx={{ width: 256, height: 256, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed grey.400', color: 'text.secondary' }}>
                <Typography variant="caption">Loading QR code...</Typography>
            </Box>
        )}
        
        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Or share the link directly:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            fullWidth
            variant="outlined"
            value={inviteLink}
            InputProps={{ readOnly: true }}
            size="small"
          />
          <Tooltip title="Copy Link">
            <IconButton onClick={handleCopyToClipboard} disabled={!inviteLink}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Button onClick={onClose} sx={{ mt: 3 }}>
          Close
        </Button>
      </Box>
    </Modal>
  );
}

export default InviteMemberModal;